import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
  BreadcrumbItem,
  BreadcrumbList,
} from "../ui/breadcrumb";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { useAudit } from "../../contexts/auditContext";
import { useAuth } from "../../contexts/authContext";
import { useApprovalConfig } from "../../hooks/useApprovalConfig";
import { useApprovalHistory } from "../../hooks/useApprovalHistory";
import { useApprovalRequestForEntity } from "../../hooks/useApprovalRequestForEntity";
import { formatStatus } from "../../lib/approvalLabels";
import { sessionRouteBase } from "../../lib/sessionApiBase";
import { Pencil, Clock, CheckCircle2, XCircle } from "lucide-react";
import { formattedDate } from "../../lib/utils";
import { TestPhases } from "../../models/audit/controlSession";
import { hasPermission, runTestPermissionFor } from "../../lib/permissions";
import { EditTestDialog } from "./EditTestDialog";
import { OptroLinkPill } from "./OptroLinkPill";
import { AuditPageHeaderActions } from "./AuditPageHeaderActions";
import { useOptroLinkErrorToast } from "../../hooks/audit/useOptroLinkErrorToast";

interface AuditPageHeaderProps {
  extraPageLabel?: string;
}

const AuditPageHeader: React.FC<AuditPageHeaderProps> = ({
  extraPageLabel,
}) => {
  const { workspaceMember, user } = useAuth();
  const { controlSession, submitForReview, reopenSession, sessionKind } =
    useAudit();
  useOptroLinkErrorToast(controlSession);

  // The session is headed by exactly one of `control` / `workStep` — the
  // widened envelope nulls the absent side. Control sessions always carry
  // `control`, so every work-step branch below is dead code for them.
  const linkedControl = controlSession?.control;
  const linkedWorkStep = controlSession?.workStep;
  // Derived from the provider's `sessionKind`, not envelope data, so this
  // never transiently reads as a control session while the work-step
  // envelope is still loading (`controlSession` — and thus `workStepId` —
  // is null/undefined during that window).
  const isWorkStepSession = sessionKind === "work_step";
  // A work-step session is gated by the work-step siblings of the control
  // session permissions, so every run/edit control below picks by kind.
  const editTestPermission = isWorkStepSession
    ? "edit_workstep_test"
    : "edit_test";
  const runTestPermission = runTestPermissionFor(sessionKind);

  // Match ControlConfigurationPageV2's `canEditControl` formula so the
  // Edit/View Control label reflects what the user will actually see on
  // the destination — `edit_control` for templates, `edit_test` for
  // instance configs, with owner-override for the demoted-admin case.
  // The linked work step is the template the session was cloned from, so
  // editing it is library governance: `edit_workstep`, no creator override.
  const canEditLinkedControl = linkedControl
    ? hasPermission(
        workspaceMember,
        linkedControl.isTemplate ? "edit_control" : "edit_test",
      ) || linkedControl.createdBy === user?.id
    : false;
  const canEditLinkedWorkStep = hasPermission(workspaceMember, "edit_workstep");
  // Approvals live under a per-kind entity type. Kept non-null for the config
  // lookup below (matching the pre-work-steps behavior while the envelope is
  // still loading); the fetch hooks get null until the session exists.
  const approvalEntityType = isWorkStepSession
    ? "work_step_session"
    : "control_session";
  const { configs, loading: loadingApprovalConfig } = useApprovalConfig({
    fetchOnLoad: true,
  });
  const { history, fetchHistory } = useApprovalHistory(
    controlSession ? approvalEntityType : null,
    controlSession?.id ?? null,
  );
  const { request: approvalRequest, refetch } = useApprovalRequestForEntity(
    controlSession ? approvalEntityType : null,
    controlSession?.id ?? null,
  );
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReopening, setIsReopening] = useState(false);
  const navigate = useNavigate();

  const currentStatus = controlSession?.status ?? "in_progress";

  const approvalRequired = configs.some(
    (c) =>
      c.entityType === approvalEntityType &&
      c.changeType === "state_transition" &&
      c.isEnabled,
  );

  const handleSubmitForReview = async () => {
    setIsSubmitting(true);
    try {
      await submitForReview();
      await Promise.all([fetchHistory(), refetch()]);
    } catch {
      // error handled in context
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReopenSession = async () => {
    setIsReopening(true);
    try {
      await reopenSession();
    } catch {
      // error handled in context
    } finally {
      setIsReopening(false);
    }
  };

  const isSessionComplete = currentStatus === "complete";

  const testPhaseLabel =
    TestPhases.find((p) => p.value === controlSession?.testPhase)?.label ??
    "Not Defined";
  const fyCycleLabel = controlSession?.fyCycle?.name ?? "—";

  const statusBadgeProps: Record<
    string,
    { label: string; variant: "secondary" | "outline" | "default" }
  > = {
    in_progress: { label: "In Progress", variant: "secondary" },
    in_review: { label: "In Review", variant: "outline" },
    complete: { label: "Complete", variant: "default" },
  };

  const badgeInfo =
    statusBadgeProps[currentStatus] ?? statusBadgeProps.in_progress;

  // Derive completed approval info from history
  const completedApprovalInfo = (() => {
    if (currentStatus !== "complete" || history.length === 0) return null;

    // History items are full approval requests. Find one that was approved/applied.
    const completedRequest = [...history]
      .reverse()
      .find((r) => r.status === "approved" || r.status === "applied");

    if (completedRequest) {
      // Look for an approve/apply action inside the request's actions
      const approveAction = completedRequest.actions
        ?.reverse()
        .find((a) => a.action === "approve" || a.action === "apply");

      if (approveAction) {
        return {
          actorEmail: completedRequest.requester?.email ?? "Deleted user",
          date: approveAction.createdAt,
          requestId: completedRequest.id,
        };
      }

      // Fallback to the request-level info
      return {
        actorEmail: completedRequest.requester?.email ?? "Deleted user",
        date: completedRequest.updatedAt,
        requestId: completedRequest.id,
      };
    }

    return null;
  })();

  return (
    <>
      <Breadcrumb>
        <BreadcrumbList>
          {controlSession && linkedControl && (
            <>
              <BreadcrumbItem>
                <BreadcrumbLink
                  className="cursor-pointer"
                  onClick={() =>
                    navigate(
                      `/audit/controls/v2?controlId=${linkedControl.duplicatedFrom}`,
                    )
                  }
                >
                  Controls
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink
                  className="cursor-pointer"
                  onClick={() =>
                    navigate(
                      `/audit/controls/v2/${linkedControl.duplicatedFrom || linkedControl.id}`,
                    )
                  }
                >
                  {linkedControl.name}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
            </>
          )}
          {controlSession && linkedWorkStep && (
            <>
              <BreadcrumbItem>
                <BreadcrumbLink
                  className="cursor-pointer"
                  onClick={() => navigate("/audit/work-steps")}
                >
                  Work Steps
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink
                  className="cursor-pointer"
                  onClick={() =>
                    navigate(
                      // Sessions run against a clone of the work step, so the
                      // breadcrumb must point at the original — not the
                      // clone `controlSession.workStepId` refers to.
                      `/audit/work-steps/${linkedWorkStep.duplicatedFrom ?? controlSession.workStepId}`,
                    )
                  }
                >
                  {linkedWorkStep.name}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
            </>
          )}
          {extraPageLabel ? (
            // Session link + extra page label
            <>
              <BreadcrumbItem>
                <BreadcrumbLink
                  className="cursor-pointer"
                  onClick={() =>
                    navigate(
                      `${sessionRouteBase(sessionKind)}/${controlSession?.id}/`,
                    )
                  }
                >
                  {controlSession?.name}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{extraPageLabel}</BreadcrumbPage>
              </BreadcrumbItem>
            </>
          ) : (
            // Session page label only
            <BreadcrumbItem>
              <BreadcrumbPage>{controlSession?.name}</BreadcrumbPage>
            </BreadcrumbItem>
          )}
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 my-2 group/title">
            <span className="text-3xl font-semibold">
              {controlSession?.name}
            </span>
            <Badge
              variant={badgeInfo.variant}
              data-testid="session-status-badge"
            >
              {badgeInfo.label}
            </Badge>
            {controlSession?.optroLink && (
              <OptroLinkPill link={controlSession.optroLink} />
            )}
            {!isSessionComplete &&
              hasPermission(workspaceMember, editTestPermission) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditDialogOpen(true)}
                  className="opacity-0 group-hover/title:opacity-100 transition-opacity h-6 w-6 p-0"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
              )}
          </div>

          {controlSession && (
            <div className="text-muted-foreground text-sm mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 group/meta">
              {controlSession.createdAt && (
                <span>Created {formattedDate(controlSession.createdAt)}</span>
              )}
              {/* Phase / FY are control-session concepts — hidden for
                  work-step-headed sessions, which carry neither. */}
              {!isWorkStepSession && (
                <>
                  <span>
                    <span className="text-muted-foreground/70">Phase:</span>{" "}
                    {testPhaseLabel}
                  </span>
                  <span>
                    <span className="text-muted-foreground/70">FY:</span>{" "}
                    {fyCycleLabel}
                  </span>
                </>
              )}
              {!isSessionComplete &&
                hasPermission(workspaceMember, editTestPermission) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditDialogOpen(true)}
                    className="opacity-0 group-hover/meta:opacity-100 transition-opacity h-6 w-6 p-0"
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                )}
            </div>
          )}
          {currentStatus === "in_review" && approvalRequest && (
            <div className="flex items-center gap-1.5 text-sm mt-1 text-muted-foreground">
              {approvalRequest.status === "pending" ? (
                <>
                  <Clock className="h-3.5 w-3.5 text-amber-500" />
                  <Link
                    to={`/audit/approvals?requestId=${approvalRequest.id}`}
                    className="hover:underline"
                  >
                    Review requested by{" "}
                    {approvalRequest.requester?.email ??
                      approvalRequest.requesterEmail ??
                      "Deleted user"}{" "}
                    on {formattedDate(approvalRequest.createdAt)} &middot;{" "}
                    {formatStatus(approvalRequest.status)}
                  </Link>
                </>
              ) : approvalRequest.status === "approved" ? (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                  <Link
                    to={`/audit/approvals?requestId=${approvalRequest.id}`}
                    className="hover:underline"
                  >
                    Approved &middot; awaiting application
                  </Link>
                </>
              ) : approvalRequest.status === "rejected" ? (
                <>
                  <XCircle className="h-3.5 w-3.5 text-red-500" />
                  <Link
                    to={`/audit/approvals?requestId=${approvalRequest.id}`}
                    className="hover:underline"
                  >
                    Review rejected
                  </Link>
                </>
              ) : null}
            </div>
          )}
          {completedApprovalInfo && (
            <div className="flex items-center gap-1.5 text-sm mt-1 text-muted-foreground">
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
              <Link
                to={`/audit/approvals?requestId=${completedApprovalInfo.requestId}`}
                className="hover:underline"
              >
                Completed by {completedApprovalInfo.actorEmail} on{" "}
                {formattedDate(completedApprovalInfo.date)}
              </Link>
            </div>
          )}
        </div>
        <AuditPageHeaderActions
          currentStatus={currentStatus}
          loadingApprovalConfig={loadingApprovalConfig}
          approvalRequired={approvalRequired}
          isSubmitting={isSubmitting}
          isReopening={isReopening}
          handleSubmitForReview={handleSubmitForReview}
          handleReopenSession={handleReopenSession}
          isSessionComplete={isSessionComplete}
          isWorkStepSession={isWorkStepSession}
          canEditLinkedControl={canEditLinkedControl}
          canEditLinkedWorkStep={canEditLinkedWorkStep}
          controlSession={controlSession}
          runTestPermission={runTestPermission}
          editTestPermission={editTestPermission}
        />
      </div>

      <EditTestDialog open={editDialogOpen} onOpenChange={setEditDialogOpen} />
    </>
  );
};

export default AuditPageHeader;
